import { useEffect, useState } from 'react';
import { ProductOffer, byBuyProduct } from '../utils/offers';

const API = process.env.REACT_APP_API_URL || '';

/**
 * The live offers, fetched once per page load and shared by every caller.
 *
 * A shop page renders thirty product cards and each wants the same answer, so
 * the first caller makes the request and the rest wait on the same promise.
 *
 * What is cached is the finished Map, not the array. Building it per caller
 * meant thirty identical Maps on every render pass — and, worse, a new object
 * identity each time, so a card could never be memoised later without the
 * memo being defeated by this hook alone.
 *
 * Held for the life of the tab rather than persisted: the feed is sent
 * no-store precisely so an admin's edit cannot leave a badge promising what
 * checkout will refuse, and a reload is the customer's own way of asking again.
 */
const EMPTY: ReadonlyMap<number, ProductOffer> = new Map();

let cache: Promise<ReadonlyMap<number, ProductOffer>> | null = null;
let resolved: ReadonlyMap<number, ProductOffer> | null = null;

function fetchOffers(): Promise<ReadonlyMap<number, ProductOffer>> {
  if (!cache) {
// packs=1: this build knows how to word a same-pack reward.
    cache = fetch(`${API}/api/product-offers/active?packs=1`)
      .then(r => r.json())
      .then(j => byBuyProduct(j.success ? (j.data as ProductOffer[]) : []))
      // An offer is a bonus. If the feed is down the shop still sells things —
      // it simply stops advertising, which beats an error wall over a promotion.
      .catch(() => EMPTY)
      .then(map => { resolved = map; return map; });
  }
  return cache;
}

/** Let a write to offers be reflected without a reload (used by the admin screen). */
export function clearOfferCache() { cache = null; resolved = null; }

export function useProductOffers(): ReadonlyMap<number, ProductOffer> {
  // Seeded from the cache so a card mounting after the first fetch renders its
  // badge immediately, rather than empty and then again a tick later.
  const [map, setMap] = useState<ReadonlyMap<number, ProductOffer>>(() => resolved ?? EMPTY);
  useEffect(() => {
    if (resolved) return;            // already have it; no state churn
    let alive = true;
    fetchOffers().then(m => { if (alive) setMap(m); });
    return () => { alive = false; };
  }, []);
  return map;
}
